import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Flatpickr from "react-flatpickr";
import { DateTime as d } from 'luxon';
import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import RoundIcon from '@material-ui/icons/FiberManualRecord';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import styles from './index.module.css';
import { MapModule } from './MapModule';
import { IAppState } from '../../../../../store';
import { GET_TELEMATIC_VEHICLES_FOR_ORGANISATION } from '../../../../../graphql/vehicles/getTelematicVehiclesForOrganisation';
import api from '../../VehicleTracking/Api';
import { useSnackBar } from '../../../../SnackBarContext/SnackBarContext';
import { SnackBarVariant } from '../../../../SnackbarWrapper/SnackbarWrapper';
import { getLocalizedDateFormat } from '../../../../../utils/localized.syntex';
import { DATE_TYPE } from '../../../../utils';
import {
  convertMilesToBranchDistance,
  convertMphToBranchSpeed,
  formatDistanceValue,
  getDistanceUnitLabel,
  getSpeedUnitLabel
} from '../../../../../utils/distance';
import {
  IReportError,
  IReportRequest,
  ITrackingReportVehicle,
  ReportStatus,
  ReportType,
  TelematicStartStopEvent,
  TelematicStartStopEventJourney,
  TERMINAL_REPORT_STATUSES
} from '../types';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

interface IFilters {
  startDate: string;
  endDate: string;
}

interface IFlatPickrProps {
  country: string;
  filters: IFilters;
  setFilters: React.Dispatch<React.SetStateAction<IFilters>>;
}

const FlatPickr = React.memo((props: IFlatPickrProps) => {
  const { country, filters, setFilters } = props;
  const snackbar = useSnackBar();
  return (
    <Flatpickr
      options={{
        mode: "range",
        static: true,
        minuteIncrement: 1,
        dateFormat: country === "United States" ? "m/d/Y" : "d/m/Y",
        defaultDate: [filters.startDate, filters.endDate],
        maxDate: d.now().toUTC().toISO()
      }}
      onChange={(dates) => {
        if (dates && dates[0] && dates[1]) {
          const from = d.fromFormat(`${dates[0].getFullYear()}/${dates[0].getMonth() + 1}/${dates[0].getDate()}`, "yyyy/M/d")
          const end = d.fromFormat(`${dates[1].getFullYear()}/${dates[1].getMonth() + 1}/${dates[1].getDate()}`, "yyyy/M/d").toUTC().toISO()
          const dayRangeCount = d.fromISO(end).diff(from, "days").days
          if (dayRangeCount <= 30) {
            setFilters({
              startDate: d.fromFormat(`${dates[0].getFullYear()}/${dates[0].getMonth() + 1}/${dates[0].getDate()}`, "yyyy/M/d").toUTC().toISO(),
              endDate: dates[1] ? d.fromFormat(`${dates[1].getFullYear()}/${dates[1].getMonth() + 1}/${dates[1].getDate()}`, "yyyy/M/d").toUTC().toISO() : ""
            })
          } else {
            setFilters({ startDate: "", endDate: "" })
            snackbar({
              message: "Selection must be 30 days or less.",
              variant: SnackBarVariant.ERROR
            })
          }
        }
      }}
      render={
        ({ defaultValue, value, ...props }: any, ref: any) => (
          <input id="TRACKING_REPORT_DATE_RANGE" {...props} defaultValue={defaultValue} ref={ref} style={{ height: 0, width: 0, overflow: "hidden", opacity: 0, position: "absolute", left: -100, top: 30 }} />
        )
      }
    />
  )
});

const formatSecondsToHour = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = Math.floor(seconds % 3600 % 60);
  const h_string = h > 9 ? `${h}` : `0${h}`;
  const m_string = m > 9 ? `${m}` : `0${m}`;
  const s_string = s > 9 ? `${s}` : `0${s}`;
  return `${h_string}:${m_string}:${s_string}`;
}

interface IVehicleReportState {
  request?: IReportRequest<TelematicStartStopEvent>;
}

const TrackingReport: React.FC = () => {
  const snackbar = useSnackBar();
  const timer = useRef<{ [vehicleId: string]: ReturnType<typeof setTimeout> }>({});
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { country } = website.organisation.address;
  // TODO: distanceUnit is actually a per-branch setting, and vehicles here can belong to any
  // branch in the org (this picker is org-wide, not branch-scoped) — hardcoded to miles for now,
  // to be sourced correctly (e.g. per-vehicle via its own branch) later.
  const branchDistanceUnit = "miles";
  const distanceUnitLabel = getDistanceUnitLabel(branchDistanceUnit);
  const speedUnitLabel = getSpeedUnitLabel(branchDistanceUnit);

  const [filters, setFilters] = useState<IFilters>({ startDate: "", endDate: "" });
  const [searchText, setSearchText] = useState<string>("");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  // Snapshot of which vehicles a Generate click covers — the results list renders from this,
  // never from the live `selectedVehicleIds` checkboxes, so ticking a new box never shows a
  // fake "processing" card for a vehicle nothing has actually been requested for yet. Clicking
  // Generate again adds whatever's newly checked to this list (accumulates, never removes).
  const [generatedVehicleIds, setGeneratedVehicleIds] = useState<string[]>([]);
  const [vehicleReports, setVehicleReports] = useState<{ [vehicleId: string]: IVehicleReportState }>({});
  const [activeVehicleId, setActiveVehicleId] = useState<string | undefined>();
  const [selectedTrip, setSelectedTrip] = useState<TelematicStartStopEventJourney | undefined>();

  // Org-wide telematics vehicle list — no business-customer/booking scoping, just "belongs to
  // this organisation" + "has telemetry" (see getTelematicVehiclesForOrganisation in
  // inventory-api). Booking-schedule lookup + date-window clipping/merging happens server-side
  // in notification-api's /report/request/vehicle now, not here.
  const [loadVehicles, { data: vehiclesData, loading: loadingVehicles, error: vehiclesError }] = useLazyQuery(GET_TELEMATIC_VEHICLES_FOR_ORGANISATION, {
    fetchPolicy: "network-only",
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error("getTelematicVehiclesForOrganisation failed", error);
      snackbar({ message: "Failed to load your vehicles", variant: SnackBarVariant.ERROR });
    }
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(timer.current).forEach((t) => clearTimeout(t));
    }
  }, []);

  const vehicles: ITrackingReportVehicle[] = useMemo(() => {
    const rows: ITrackingReportVehicle[] = vehiclesData?.getTelematicVehiclesForOrganisation || [];
    return rows
      .filter((vehicle) => !vehicle.isGhostVehicle && vehicle.telemetryDeviceId)
      .sort((a, b) => a.licencePlate.localeCompare(b.licencePlate));
  }, [vehiclesData]);

  const filteredVehicles = vehicles.filter((vehicle) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return [vehicle.licencePlate, vehicle.make, vehicle.model].filter(Boolean).some((v) => v!.toLowerCase().includes(q));
  });

  const toggleVehicle = (vehicleId: string) => {
    setSelectedVehicleIds((prev) => prev.includes(vehicleId) ? prev.filter((id) => id !== vehicleId) : [...prev, vehicleId]);
  }

  const pollReportRequest = useCallback((vehicleId: string, groupId: string) => {
    api.getVehicleReportRequestGroup(groupId).then(({ data }: { data: IReportRequest<TelematicStartStopEvent> }) => {
      setVehicleReports((prev) => ({ ...prev, [vehicleId]: { request: data } }));
      if (!TERMINAL_REPORT_STATUSES.includes(data.status)) {
        timer.current[vehicleId] = setTimeout(() => pollReportRequest(vehicleId, groupId), 3000);
      }
    }).catch(() => {
      snackbar({ message: "Failed to load trip history", variant: SnackBarVariant.ERROR });
    });
  }, []);

  const handleGenerate = async () => {
    if (!selectedVehicleIds.length || !filters.startDate || !filters.endDate || !website.tenantId || !website.organisationId) {
      return;
    }
    setGeneratedVehicleIds((prev) => Array.from(new Set([...prev, ...selectedVehicleIds])));
    setActiveVehicleId(undefined);
    const startDate = d.fromISO(filters.startDate).toUTC().toISO();
    const endDate = d.min(d.fromISO(filters.endDate).endOf("day"), d.now()).toUTC().toISO();

    for (const vehicleId of selectedVehicleIds) {
      const vehicle = vehicles.find((v) => v.id === vehicleId);
      if (!vehicle || !vehicle.telemetryDeviceId) continue;

      try {
        const { data }: { data: IReportRequest<TelematicStartStopEvent> } = await api.createVehicleReportRequest({
          vehicleId: vehicle.id,
          deviceEsn: vehicle.telemetryDeviceId,
          startDate,
          endDate,
          reportType: ReportType.TRACKING,
          tenantId: website.tenantId,
          organisationId: website.organisationId,
          branchId: vehicle.branchId
        });
        setVehicleReports((prev) => ({ ...prev, [vehicleId]: { request: data } }));
        if (!TERMINAL_REPORT_STATUSES.includes(data.status)) {
          timer.current[vehicleId] = setTimeout(() => pollReportRequest(vehicleId, data.id), 3000);
        }
      } catch (error: any) {
        snackbar({
          message: error?.message === "Request failed with status code 403" ? "Access denied" : "Failed to generate trip history",
          variant: SnackBarVariant.ERROR
        });
      }
    }
  }

  const formatMilesForBranch = (miles: number, unit: string | undefined, fractionDigits = 2) =>
    formatDistanceValue(convertMilesToBranchDistance(Number(miles || 0), unit), fractionDigits);

  const formatMphForBranch = (mph: number, unit: string | undefined, fractionDigits = 1) =>
    formatDistanceValue(convertMphToBranchSpeed(Number(mph || 0), unit), fractionDigits);

  const makeCsv = (vehicle: ITrackingReportVehicle, event: TelematicStartStopEvent) => {
    const unit = branchDistanceUnit;
    const separator = ',';
    const columns = [
      "Vehicle",
      "Start time",
      "End time",
      "Start location",
      "End location",
      "Start lat-long",
      "End lat-long",
      "Running time",
      "Idle time",
      `Max speed (${speedUnitLabel})`,
      `Average speed (${speedUnitLabel})`,
      `Distance (${distanceUnitLabel})`,
      `Odometer (${distanceUnitLabel})`,
      "Fuel consumed (L)",
      "Harsh Braking",
      "Harsh Acceleration",
      "Hard Cornering",
      "Speeding",
      "Lost GPS",
      "Lost GSM",
    ];
    const csvContent = `${columns.join(separator)}\n${event.journeys.map(journey => {
      const cells = [
        vehicle.licencePlate,
        getLocalizedDateFormat(country, journey.sTime, DATE_TYPE.EXPANDED).replace(',', ''),
        getLocalizedDateFormat(country, journey.eTime, DATE_TYPE.EXPANDED).replace(',', ''),
        journey.startStreet || "",
        journey.endStreet || "",
        `"${journey.startLat} , ${journey.startLong}"`,
        `"${journey.endLat} , ${journey.endLong}"`,
        formatSecondsToHour(journey.duration - journey.idleTime),
        formatSecondsToHour(journey.idleTime),
        formatMphForBranch(journey.maxSpeed, unit),
        formatMphForBranch(journey.avgSpeed, unit),
        formatMilesForBranch(journey.miles, unit),
        journey.odometer ? formatMilesForBranch(journey.odometer, unit) : "N/A",
        journey.fuelConsumed !== null && journey.fuelConsumed !== undefined ? journey.fuelConsumed : "N/A",
        journey.harshBraking,
        journey.harshAcceleration,
        journey.hardCornering,
        journey.speeding,
        journey.lostGPS,
        journey.lostGSM,
      ];
      return cells.join(separator);
    }).join('\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `Tripsdata-${vehicle.licencePlate}-${getLocalizedDateFormat(country, filters.startDate, DATE_TYPE.CONDENSED)}-${getLocalizedDateFormat(country, filters.endDate, DATE_TYPE.CONDENSED)}`;
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  const activeVehicle = activeVehicleId ? vehicles.find((v) => v.id === activeVehicleId) : undefined;
  const activeReport = activeVehicleId ? vehicleReports[activeVehicleId]?.request : undefined;
  const activeEvent: TelematicStartStopEvent | undefined = activeReport?.status === ReportStatus.COMPLETED && Array.isArray(activeReport.data)
    ? activeReport.data[0]
    : undefined;
  const activeReportError: IReportError | undefined = activeReport?.status === ReportStatus.FAILED && activeReport.data && !Array.isArray(activeReport.data)
    ? activeReport.data
    : undefined;

  const canPickDateRange = selectedVehicleIds.length > 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.banner}>
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          Report generated will be available for 24 hours before it is removed from the list below. Please note that it may take several minutes to prepare the report depending on the date range and number of vehicles selected.
        </Typography>
      </div>
      <div className={styles.columns}>
        <div className={styles.leftColumn}>
          <Typography variant="h6" style={{ marginBottom: 10 }}>Vehicles</Typography>
          <TextField
            className={styles.searchInput}
            size="small"
            variant="outlined"
            fullWidth
            placeholder="Search vehicles"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className={styles.vehicleListCard}>
            {loadingVehicles ? (
              <Grid container justify="center" style={{ padding: 16 }}>
                <CircularProgress size={20} thickness={5} />
              </Grid>
            ) : vehiclesError ? (
              <Typography variant="body2" style={{ padding: "8px 4px", color: "#d32f2f" }}>Couldn't load your vehicles. Please try again.</Typography>
            ) : filteredVehicles.length ? (
              filteredVehicles.map((vehicle, index) => (
                <React.Fragment key={vehicle.id}>
                  {index > 0 && <Divider />}
                  <div className={styles.vehicleRow} onClick={() => toggleVehicle(vehicle.id)}>
                    <Checkbox
                      size="small"
                      checked={selectedVehicleIds.includes(vehicle.id)}
                      onClick={(e) => { e.stopPropagation(); toggleVehicle(vehicle.id); }}
                    />
                    <div>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>{vehicle.licencePlate}</Typography>
                      {(vehicle.make || vehicle.model) && (
                        <Typography variant="body2" style={{ color: "#848484" }}>{vehicle.make} {vehicle.model}</Typography>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              ))
            ) : (
              <Typography variant="body2" style={{ padding: "8px 4px" }}>No telematics-enabled vehicles found for your organisation.</Typography>
            )}
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.vehicleResults}>
            <div className="flex space-between" style={{ marginBottom: 10 }}>
              <Tooltip title={canPickDateRange ? "" : "Select at least one vehicle first"}>
                <div style={{ position: "relative", width: 150, opacity: canPickDateRange ? 1 : 0.5, pointerEvents: canPickDateRange ? "auto" : "none" }}>
                  <label htmlFor="TRACKING_REPORT_DATE_RANGE" className={styles.filterInput}>
                    {filters.startDate ? getLocalizedDateFormat(country, filters.startDate, DATE_TYPE.CONDENSED) : "From date"} to {filters.endDate ? getLocalizedDateFormat(country, filters.endDate, DATE_TYPE.CONDENSED) : "To date"}
                  </label>
                  <div style={{ position: "absolute", left: 0, right: 0 }}>
                    <FlatPickr filters={filters} country={country} setFilters={setFilters} />
                  </div>
                </div>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                disabled={!selectedVehicleIds.length || !filters.startDate || !filters.endDate}
                onClick={handleGenerate}
              >
                Generate
              </Button>
            </div>

            {generatedVehicleIds.length ? (
              <div className={styles.scroll}>
                {generatedVehicleIds.map((vehicleId) => {
                  const vehicle = vehicles.find((v) => v.id === vehicleId);
                  const state = vehicleReports[vehicleId];
                  if (!vehicle) return null;
                  const request = state?.request;
                  const completed = request?.status === ReportStatus.COMPLETED;
                  const event = completed && Array.isArray(request?.data) ? request.data[0] : undefined;
                  const vehicleUnit = branchDistanceUnit;
                  return (
                    <div
                      key={vehicleId}
                      className={`${styles.result} ${activeVehicleId === vehicleId ? styles.selected : ""}`}
                      style={{ pointerEvents: completed ? "auto" : "none" }}
                      onClick={() => completed && setActiveVehicleId(vehicleId)}
                    >
                      <Grid container xs={12} style={{ position: "relative" }}>
                        <Typography variant="h4">{vehicle.licencePlate}</Typography>
                      </Grid>
                      {!request || !TERMINAL_REPORT_STATUSES.includes(request.status) ? (
                        <div className="flex cross-center" style={{ marginTop: 5 }}>
                          <CircularProgress size={14} thickness={5} style={{ marginRight: 8 }} />
                          <Typography variant="body2" style={{ textTransform: "capitalize" }}>{(request?.status || ReportStatus.QUEUED).toLowerCase()}</Typography>
                        </div>
                      ) : request.status === ReportStatus.FAILED ? (
                        <Typography variant="body2" style={{ color: "#d32f2f" }}>
                          {request.data && !Array.isArray(request.data) ? (request.data as IReportError).message : "Failed to generate report."}
                        </Typography>
                      ) : event ? (
                        <Grid container justify="space-between" alignItems="center" style={{ marginTop: 5 }}>
                          <span style={{ color: "#848484" }}>
                            <RoundIcon color="inherit" style={{ fontSize: 14, marginLeft: -3, color: "#6CC38A" }} />
                            {`${event.numOfJourneys} trips (${formatMilesForBranch(event.totalMiles, vehicleUnit)} ${getDistanceUnitLabel(vehicleUnit)})`}
                          </span>
                          {event.numOfJourneys > 0 && (
                            <Tooltip title="Download report">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); makeCsv(vehicle, event); }}>
                                <CloudDownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Grid>
                      ) : (
                        <Typography variant="body2" style={{ color: "#848484" }}>No trips recorded.</Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography variant="body2">Select vehicles and a date range, then click Generate.</Typography>
            )}
          </div>

          {activeVehicleId && activeVehicle && (
            <div className={styles.rightSub}>
              <div className="flex fill">
                <MapModule trip={selectedTrip} />
              </div>
              <div className={`${styles.tableWrapper} flex fill col-flex padding`}>
                <Typography variant="h6" className={styles.tripTableTitle}>
                  Trip history for {activeVehicle.licencePlate}
                </Typography>
                {activeReportError ? (
                  <Typography variant="body2" style={{ color: "#d32f2f" }}>{activeReportError.message}</Typography>
                ) : activeEvent && activeEvent.journeys.length ? (() => {
                  const activeUnit = branchDistanceUnit;
                  const activeDistanceUnitLabel = getDistanceUnitLabel(activeUnit);
                  const activeSpeedUnitLabel = getSpeedUnitLabel(activeUnit);
                  return (
                  <table className={styles.tripTable}>
                    <thead>
                      <tr>
                        <th>From</th>
                        <th>To</th>
                        <th>{`Distance (${activeDistanceUnitLabel})`}</th>
                        <th>Running time</th>
                        <th>Idle time</th>
                        <th>{`Avg speed (${activeSpeedUnitLabel})`}</th>
                        <th>{`Max speed (${activeSpeedUnitLabel})`}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeEvent.journeys.map((journey) => {
                        const selected = selectedTrip?.sTime === journey.sTime && selectedTrip.eTime === journey.eTime;
                        return (
                          <tr
                            key={journey.sTime}
                            onClick={() => setSelectedTrip(journey)}
                            className={`${styles.trip} ${selected ? styles.selected : ""}`}
                          >
                            <td>
                              <Typography variant="body1">{journey.startStreet || ""}</Typography>
                              <Typography variant="body2">{getLocalizedDateFormat(country, journey.sTime, DATE_TYPE.EXPANDED)}</Typography>
                            </td>
                            <td>
                              <Typography variant="body1">{journey.endStreet || ""}</Typography>
                              <Typography variant="body2">{getLocalizedDateFormat(country, journey.eTime, DATE_TYPE.EXPANDED)}</Typography>
                            </td>
                            <td><Typography variant="body2">{`${formatMilesForBranch(journey.miles, activeUnit)} ${activeDistanceUnitLabel}`}</Typography></td>
                            <td><Typography variant="body2">{formatSecondsToHour(journey.duration - journey.idleTime)}</Typography></td>
                            <td><Typography variant="body2">{formatSecondsToHour(journey.idleTime)}</Typography></td>
                            <td><Typography variant="body2">{`${formatMphForBranch(journey.avgSpeed, activeUnit)} ${activeSpeedUnitLabel}`}</Typography></td>
                            <td><Typography variant="body2">{`${formatMphForBranch(journey.maxSpeed, activeUnit)} ${activeSpeedUnitLabel}`}</Typography></td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  );
                })() : (
                  <Typography variant="body2">No trips recorded for this vehicle.</Typography>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrackingReport;
